"use client";

import { useCallback, useEffect, useState } from "react";
import { SAVED_PLACES_EVENT, SAVED_PLACES_KEY, SAVED_PLACES_LIMIT, cleanSlugs } from "@/lib/virtualspace/guide/guide-lists";

/**
 * Saved places on this device (localStorage), shared by every button and
 * the header count through one event. The server renders nothing saved, so
 * the first paint matches; the list fills in after mount.
 */

function readSaved(): string[] {
  try {
    return cleanSlugs(JSON.parse(window.localStorage.getItem(SAVED_PLACES_KEY) ?? "[]"));
  } catch {
    return [];
  }
}

function writeSaved(slugs: string[]) {
  try {
    window.localStorage.setItem(SAVED_PLACES_KEY, JSON.stringify(cleanSlugs(slugs)));
  } catch {
    // Private windows and blocked storage: the list lives for the page only.
  }
  window.dispatchEvent(new CustomEvent(SAVED_PLACES_EVENT));
}

export function useSavedPlaces(): { saved: string[]; ready: boolean; toggle: (slug: string) => void; add: (slugs: string[]) => void; clear: () => void } {
  const [saved, setSaved] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const sync = () => setSaved(readSaved());
    sync();
    setReady(true);
    window.addEventListener(SAVED_PLACES_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SAVED_PLACES_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const toggle = useCallback((slug: string) => {
    const current = readSaved();
    writeSaved(current.includes(slug) ? current.filter((s) => s !== slug) : [slug, ...current].slice(0, SAVED_PLACES_LIMIT));
  }, []);
  const add = useCallback((slugs: string[]) => {
    const current = readSaved();
    writeSaved([...current, ...slugs.filter((s) => !current.includes(s))].slice(0, SAVED_PLACES_LIMIT));
  }, []);
  const clear = useCallback(() => writeSaved([]), []);
  return { saved, ready, toggle, add, clear };
}

/** Save / Saved on a place; a heart that fills. */
export function GuideSaveButton({
  slug,
  copy,
  size = "md",
  style,
}: {
  slug: string;
  copy: { save: string; saved: string; unsave: string };
  size?: "sm" | "md";
  style?: React.CSSProperties;
}) {
  const { saved, toggle } = useSavedPlaces();
  const on = saved.includes(slug);
  return (
    <button
      type="button"
      className={`eg-save${on ? " eg-save--on" : ""}${size === "sm" ? " eg-save--sm" : ""}`}
      aria-pressed={on}
      aria-label={on ? copy.unsave : copy.save}
      title={on ? copy.unsave : copy.save}
      data-eg-click={on ? undefined : "save"}
      data-eg-subject={slug}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        toggle(slug);
      }}
      style={style}
    >
      <svg aria-hidden="true" width={size === "sm" ? 14 : 18} height={size === "sm" ? 14 : 18} viewBox="0 0 24 24">
        <path
          d="M12 21s-7.5-4.6-9.6-9.3C1 8.3 3.3 4.5 7 4.5c2 0 3.4 1 5 2.8 1.6-1.8 3-2.8 5-2.8 3.7 0 6 3.8 4.6 7.2C19.5 16.4 12 21 12 21z"
          fill={on ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
      <span>{on ? copy.saved : copy.save}</span>
    </button>
  );
}

/** The header's link to the saved places, with the count once known. */
export function GuideSavedLink({ href, label, style }: { href: string; label: string; style?: React.CSSProperties }) {
  const { saved, ready } = useSavedPlaces();
  return (
    <a href={href} style={style} className="eg-saved-link">
      {label}
      {ready && saved.length > 0 ? <span className="eg-saved-link__count">{saved.length}</span> : null}
    </a>
  );
}
